import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronDown, Minus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import * as servicesApi from '../services/servicesApi';
import useModalBackButton from '../hooks/useModalBackButton';
import ModalShell from './ModalShell';
import { ModalListSkeleton } from './Skeleton';
import { haptic } from '../utils/vibration';
import { getServiceIconComponent } from '../utils/serviceIcons';

const normalizeItemServiceType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  const aliases = {
    laundry: 'washing',
    washing: 'washing',
    'dry-cleaning': 'cleaning',
    'dry cleaning': 'cleaning',
    drycleaning: 'cleaning',
    cleaning: 'cleaning'
  };
  return aliases[normalized] || normalized;
};

/**
 * Модалка выбора предметов — группы → предметы
 * @param {Array} existingItems - Уже добавленные предметы в корзине
 */
const ItemSelectorModal = ({
  open,
  onClose,
  onAdd,
  onRemoveLast,
  onReplace,
  existingItems = [],
  clientType = 'b2c',
  serviceType = '',
  priceAt = null,
  priceCoefficient = 1
}) => {
  const [groups, setGroups] = useState([]);
  const [services, setServices] = useState([]);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  useModalBackButton(open, onClose);

  const displayPrice = (price) => (Number(price || 0) * Number(priceCoefficient || 1)).toFixed(0);
  const getServiceRegularPrice = (service) => service?.regular_price ?? service?.base_price ?? 0;
  const getServicePromotionPrice = (service) => service?.promotion_price ?? null;
  const getServiceFinalPrice = (service) => service?.final_price ?? service?.current_price ?? service?.base_price ?? 0;

  // Определяем тип услуги для фильтрации
  const getServiceType = () => {
    const normalizedType = normalizeItemServiceType(serviceType);
    if (normalizedType === 'cleaning') return ['cleaning'];
    if (normalizedType === 'washing') return ['washing'];
    return ['washing', 'cleaning'];
  };

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, clientType, priceAt]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [groupsData, servicesData] = await Promise.all([
        servicesApi.getServiceGroups({ active_only: true, client_type: clientType }),
        servicesApi.getServices({ active_only: true, client_type: clientType, price_at: priceAt })
      ]);

      // Фильтруем группы по типу услуги
      const allowedTypes = getServiceType();
      const filteredGroups = groupsData.filter(g => allowedTypes.includes(g.service_type));
      const filteredServices = servicesData.filter(s => allowedTypes.includes(s.service_type));

      setGroups(filteredGroups);
      setServices(filteredServices);
      setExpandedGroup(null);
    } catch (err) {
      console.error('Ошибка загрузки услуг:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (service) => {
    haptic('success');
    // Проверяем allow_multiple группы
    const group = groups.find(g => g.id === service.group_id);
    const allowMultiple = group ? group.allow_multiple : true;

    if (!allowMultiple && service.group_id) {
      // Ищем предмет из этой же группы в корзине
      const existingItem = existingItems.find(
        item => item.groupId === service.group_id && !item.isAdditional
      );

      if (existingItem) {
        // Заменяем предмет из той же группы
        if (typeof onReplace === 'function') {
          onReplace(existingItem.id, {
            service_id: service.id,
            name: service.name,
            price: getServiceFinalPrice(service),
            regular_price: getServiceRegularPrice(service),
            promotion_price: getServicePromotionPrice(service),
            unit: service.unit,
            icon: service.icon || null,
            quantity: 1,
            groupId: service.group_id
          });
        } else {
          onAdd({
            service_id: service.id,
            name: service.name,
            price: getServiceFinalPrice(service),
            regular_price: getServiceRegularPrice(service),
            promotion_price: getServicePromotionPrice(service),
            unit: service.unit,
            icon: service.icon || null,
            quantity: 1,
            groupId: service.group_id
          });
        }
        return;
      }
    }

    // Добавляем новый предмет
    onAdd({
      service_id: service.id,
      name: service.name,
      price: getServiceFinalPrice(service),
      regular_price: getServiceRegularPrice(service),
      promotion_price: getServicePromotionPrice(service),
      unit: service.unit,
      icon: service.icon || null,
      quantity: 1,
      groupId: service.group_id
    });
  };

  const handleRemoveLastClick = (event, serviceId) => {
    event.stopPropagation();
    haptic('tap');
    onRemoveLast?.(serviceId);
  };

  // Предметы без группы
  const ungroupedServices = services.filter(s => !s.group_id);

  return (
    <ModalShell
      open={open}
      onClose={() => {
        haptic('close');
        onClose();
      }}
      variant="sheet"
      expandable={true}
      panelClassName="modal-bottom-sheet flex min-h-0 max-h-[75vh] flex-col overflow-hidden sm:max-w-lg"
      panelProps={{ style: { maxHeight: '75dvh', overflowY: 'hidden' } }}
    >
        {/* Заголовок */}
        <div className="sticky top-0 z-10 shrink-0 border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Выберите предмет</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {clientType === 'b2b' ? 'Юр. лица' : 'Физ. лица'}
              {Number(priceCoefficient || 1) !== 1 ? ` · коэф. ×${priceCoefficient}` : ''}
            </p>
          </div>
          <button onClick={() => { haptic('close'); onClose(); }} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {loading ? (
            <ModalListSkeleton count={5} />
          ) : (
            <div className="space-y-2">
              {/* Группы */}
              {groups.map(group => {
                const previewService = services.find((service) => service.group_id === group.id && service.icon);
                const GroupIcon = getServiceIconComponent(previewService?.icon);
                const groupItemsCount = existingItems.filter(
                  (item) => !item.isAdditional && services.filter((service) => service.group_id === group.id).some((service) => service.id === item.service_id)
                ).length;

                return (
                <div key={group.id} className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                  <button
                    onClick={() => {
                      haptic(expandedGroup === group.id ? 'close' : 'open');
                      setExpandedGroup(expandedGroup === group.id ? null : group.id);
                    }}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <GroupIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      <span className="font-medium text-gray-900 dark:text-gray-100">{group.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {groupItemsCount > 0 ? (
                        <span className="min-w-[20px] h-5 px-1.5 bg-blue-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {groupItemsCount}
                        </span>
                      ) : null}
                      {expandedGroup === group.id ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </button>

                  <AnimatePresence>
                    {expandedGroup === group.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                          {services
                            .filter(s => s.group_id === group.id)
                            .map(service => {
                              const count = existingItems.filter(i => !i.isAdditional && i.service_id === service.id).length;
                              const ServiceIcon = getServiceIconComponent(service.icon);
                              return (
                                <button
                                  key={service.id}
                                  type="button"
                                  onClick={() => handleItemClick(service)}
                                  className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                  <div className="flex min-w-0 flex-1 items-center gap-3">
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300">
                                      <ServiceIcon className="h-4.5 w-4.5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{service.name}</div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {service.unit === 'шт' ? 'Шт.' : service.unit} • {getServicePromotionPrice(service) !== null ? `${displayPrice(getServiceFinalPrice(service))} ₽ вместо ${displayPrice(getServiceRegularPrice(service))} ₽` : `${displayPrice(getServiceFinalPrice(service))} ₽`}
                                      </div>
                                    </div>
                                  </div>
                                  {count > 0 ? (
                                    <div className="ml-2 flex items-center gap-1.5 shrink-0">
                                      <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={(event) => handleRemoveLastClick(event, service.id)}
                                        onKeyDown={(event) => {
                                          if (event.key === 'Enter' || event.key === ' ') {
                                            handleRemoveLastClick(event, service.id);
                                          }
                                        }}
                                        className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                                        aria-label="Удалить последний добавленный предмет"
                                      >
                                        <Minus className="h-3.5 w-3.5" />
                                      </span>
                                      <span className="min-w-[24px] h-6 px-1.5 bg-blue-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                                        {count}
                                      </span>
                                    </div>
                                  ) : (
                                    <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                                  )}
                                </button>
                              );
                            })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                );
              })}

              {/* Предметы без группы */}
              {ungroupedServices.length > 0 && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                  <div className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400">
                    Без группы
                  </div>
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {ungroupedServices.map(service => {
                      const count = existingItems.filter(i => !i.isAdditional && i.service_id === service.id).length;
                      const ServiceIcon = getServiceIconComponent(service.icon);
                      return (
                        <button
                          key={service.id}
                          type="button"
                          onClick={() => handleItemClick(service)}
                          className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <div className="flex min-w-0 flex-1 items-center gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300">
                              <ServiceIcon className="h-4.5 w-4.5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{service.name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                      {service.unit === 'шт' ? 'Шт.' : service.unit} • {getServicePromotionPrice(service) !== null ? `${displayPrice(getServiceFinalPrice(service))} ₽ вместо ${displayPrice(getServiceRegularPrice(service))} ₽` : `${displayPrice(getServiceFinalPrice(service))} ₽`}
                              </div>
                            </div>
                          </div>
                          {count > 0 ? (
                            <div className="ml-2 flex items-center gap-1.5 shrink-0">
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(event) => handleRemoveLastClick(event, service.id)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    handleRemoveLastClick(event, service.id);
                                  }
                                }}
                                className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                                aria-label="Удалить последний добавленный предмет"
                              >
                                <Minus className="h-3.5 w-3.5" />
                              </span>
                              <span className="min-w-[24px] h-6 px-1.5 bg-blue-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                                {count}
                              </span>
                            </div>
                          ) : (
                            <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {groups.length === 0 && ungroupedServices.length === 0 && (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">Нет доступных предметов</p>
              )}
            </div>
          )}
        </div>
    </ModalShell>
  );
};

export default ItemSelectorModal;
